// PDP Two-Column Layout: Anchor Navigation
// - Tracks active section as user scrolls
// - Smooth scrolling with focus management
// - Respects prefers-reduced-motion

(function () {
  'use strict';

  const ANCHOR_NAV_CLASS = 'pdp-two-col__anchor-nav';
  const ANCHOR_LINK_SELECTOR = '[data-anchor-link]';
  const ACTIVE_CLASS = 'pdp-two-col__anchor-nav__link--active';
  const SECTION_SELECTOR = '[data-pdp-section]';
  const SIDEBAR_SELECTOR = '[data-pdp-sidebar]';
  const NAV_BAR_SELECTOR = '#nav-bar';
  const NAV_FALLBACK_SELECTOR = '#nav';
  const SIDEBAR_GAP_PX = 16;

  class PDPAnchorNav {
    constructor() {
      this.nav = document.querySelector(`.${ANCHOR_NAV_CLASS}`);
      if (!this.nav) return;
      this.sidebar = document.querySelector(SIDEBAR_SELECTOR);

      this.links = Array.from(this.nav.querySelectorAll(ANCHOR_LINK_SELECTOR));
      this.sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));

      if (this.links.length === 0 || this.sections.length === 0) return;

      this.init();
    }

    init() {
      this.syncSidebarOffset();
      this.attachEventListeners();
      this.observeSections();
      this.updateActiveLink();
    }

    attachEventListeners() {
      this.links.forEach((link) => {
        link.addEventListener('click', (e) => this.handleLinkClick(e));
      });

      window.addEventListener('resize', () => this.syncSidebarOffset(), { passive: true });
      window.addEventListener('scroll', () => this.syncSidebarOffset(), { passive: true });
    }

    handleLinkClick(e) {
      e.preventDefault();
      const href = e.currentTarget.getAttribute('href');
      const target = document.querySelector(href);

      if (!target) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const stickyOffset = this.getStickyOffset();
      const targetTop = target.getBoundingClientRect().top + window.scrollY - stickyOffset;

      if (prefersReducedMotion) {
        window.scrollTo({ top: targetTop });
      } else {
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      }

      // Move focus to target for accessibility
      target.setAttribute('tabindex', '-1');
      if (typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }

      // Update active link
      this.setActiveLink(href);
    }

    observeSections() {
      const options = {
        rootMargin: '0px 0px -66% 0px', // Treat top 1/3 of viewport as "visible"
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('id');
            const href = `#${sectionId}`;
            this.setActiveLink(href);
          }
        });
      }, options);

      this.sections.forEach((section) => observer.observe(section));
    }

    setActiveLink(href) {
      this.links.forEach((link) => {
        link.classList.remove(ACTIVE_CLASS);
        if (link.getAttribute('href') === href) {
          link.classList.add(ACTIVE_CLASS);
        }
      });
    }

    getStickyOffset() {
      const sidebar = this.sidebar;
      const sidebarTop = sidebar
        ? Number.parseFloat(window.getComputedStyle(sidebar).top)
        : Number.NaN;

      if (Number.isFinite(sidebarTop) && sidebarTop > 0) {
        return sidebarTop;
      }

      return this.getNavHeight() + SIDEBAR_GAP_PX;
    }

    syncSidebarOffset() {
      if (!this.sidebar) return;
      const stickyTop = this.getNavHeight() + SIDEBAR_GAP_PX;
      this.sidebar.style.setProperty('--_sidebar-sticky-top', `${stickyTop}px`);
    }

    getNavHeight() {
      const navBar = document.querySelector(NAV_BAR_SELECTOR);
      if (navBar && navBar.offsetHeight > 0) {
        return navBar.offsetHeight;
      }

      const navFallback = document.querySelector(NAV_FALLBACK_SELECTOR);
      if (navFallback && navFallback.offsetHeight > 0) {
        return navFallback.offsetHeight;
      }

      const bodyStickyNav = Number.parseFloat(
        window.getComputedStyle(document.body).getPropertyValue('--sticky-nav')
      );
      if (Number.isFinite(bodyStickyNav) && bodyStickyNav > 0) {
        return bodyStickyNav;
      }

      const rootStickyNav = Number.parseFloat(
        window.getComputedStyle(document.documentElement).getPropertyValue('--sticky-nav')
      );
      if (Number.isFinite(rootStickyNav) && rootStickyNav > 0) {
        return rootStickyNav;
      }

      return 0;
    }

    updateActiveLink() {
      if (this.links.length > 0) {
        this.links[0].classList.add(ACTIVE_CLASS);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new PDPAnchorNav();
  });
})();
